import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase_client';
import { useCompany } from '../contexts/CompanyContext';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const purchaseId = searchParams.get('purchase_id');
  const provider = searchParams.get('provider');
  
  // Check Billplz redirect parameters
  const billplzPaid = searchParams.get('billplz[paid]');
  const hitpayStatus = searchParams.get('status');
  const [redirecting, setRedirecting] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false);
  const { selectedCompany } = useCompany();
  
  // Prevent multiple executions
  const hasProcessed = useRef(false);
  const companyIdRef = useRef<string | null>(null); // Store company ID from purchase

  const verifyAndRedirect = useCallback(async () => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }

    hasProcessed.current = true; // Mark as processed immediately
  
    // Helper function to get company ID
    const getCompanyId = async (): Promise<string | null> => {
      // First try selectedCompany
      if (selectedCompany?.company_id) {
        return selectedCompany.company_id;
      }
      
      // If we already fetched it, use cached value
      if (companyIdRef.current) {
        return companyIdRef.current;
      }
      
      // Try to get from purchase if we have purchaseId
      if (purchaseId) {
        try {
          const { data: purchase, error } = await supabase
            .from('org_purchases')
            .select('org_id')
            .eq('id', purchaseId)
            .single();
          
          if (!error && purchase?.org_id) {
            companyIdRef.current = purchase.org_id;
            return purchase.org_id;
          }
        } catch (error) {
          console.error('Error getting company from purchase:', error);
        }
      }
      
      return null;
    };

    // If Billplz provider and payment was explicitly not paid, redirect immediately
    if (provider === 'billplz' && billplzPaid === 'false' && purchaseId) {     
      // Redirect IMMEDIATELY - no waiting, no email
      navigate(`/payment-canceled?purchase_id=${purchaseId}`, { replace: true });
      return;
    }

    // If Billplz says payment is paid, wait briefly for callback, then proceed
    if (provider === 'billplz' && billplzPaid === 'true' && purchaseId) {
      // Get company ID early
      const companyId = await getCompanyId();
      if (companyId) {
        companyIdRef.current = companyId;
      }
      
      // Wait a short time for callback to process (2 seconds)
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Check database status with retries (but don't wait too long)
      let isPaid = false;
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second
      
      for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
          const { data: purchase, error } = await supabase
            .from('org_purchases')
            .select('id, status, org_id')
            .eq('id', purchaseId)
            .single();

          if (error) {
            console.error('Error fetching purchase:', error);
          } else if (purchase?.status === 'paid') {
            isPaid = true;
            // Store company ID from purchase
            if (purchase.org_id) {
              companyIdRef.current = purchase.org_id;
            }
            break;
          } else {
            // Store company ID even if not paid yet
            if (purchase?.org_id) {
              companyIdRef.current = purchase.org_id;
            }
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
        
        // Wait before next retry (except on last attempt)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }

      // If still not paid after retries, log warning but proceed (Billplz said it's paid)
      if (!isPaid) {
        console.warn('⚠️ Payment status still not "paid" after retries, but Billplz says it\'s paid. Proceeding with success.');
      }

      // Payment is successful
      setVerifying(false);
      setIsPaymentSuccessful(true);
      setRedirecting(true);
      
      // Get final company ID (try selectedCompany first, then cached value)
      const finalCompanyId = selectedCompany?.company_id || companyIdRef.current || await getCompanyId();
      
      setTimeout(() => {
        if (finalCompanyId) {
          navigate(`/all-filings/${finalCompanyId}?payment_success=true`, { replace: true });
        } else {
          console.error('No company ID available, redirecting to signin');
          navigate('/signin', { replace: true });
        }
      }, 3000);
      return;
    }

    // HitPay: redirect has status=completed or status=failed
    if (provider === 'hitpay' && purchaseId) {
      if (hitpayStatus === 'failed' || hitpayStatus === 'canceled' || hitpayStatus === 'expired') {
        navigate(`/payment-canceled?purchase_id=${purchaseId}`, { replace: true });
        return;
      }
      if (hitpayStatus === 'completed') {
        const companyId = await getCompanyId();
        if (companyId) companyIdRef.current = companyId;
        await new Promise((r) => setTimeout(r, 2000));
        let isPaid = false;
        for (let attempt = 0; attempt <= 3; attempt++) {
          const { data: p } = await supabase
            .from('org_purchases')
            .select('id, status, org_id')
            .eq('id', purchaseId)
            .single();
          if (p?.status === 'paid') {
            isPaid = true;
            if (p.org_id) companyIdRef.current = p.org_id;
            break;
          }
          if (p?.org_id) companyIdRef.current = p.org_id;
          if (attempt < 3) await new Promise((r) => setTimeout(r, 1000));
        }
        setVerifying(false);
        setIsPaymentSuccessful(true);
        setRedirecting(true);
        const finalCompanyId = selectedCompany?.company_id || companyIdRef.current || await getCompanyId();
        setTimeout(() => {
          if (finalCompanyId) {
            navigate(`/all-filings/${finalCompanyId}?payment_success=true`, { replace: true });
          } else {
            navigate('/signin', { replace: true });
          }
        }, 3000);
        return;
      }
      // No status param: fall through to DB check below (e.g. wait for webhook)
    }
    
    // For non-Billplz or when billplz[paid] parameter is missing, check database
    if (purchaseId) {
      try {
        const { data: purchase, error } = await supabase
          .from('org_purchases')
          .select('id, status, payment_reference, payment_provider, org_id')
          .eq('id', purchaseId)
          .single();

        if (error) {
          console.error('Error fetching purchase:', error);
          setVerifying(false);
          setIsPaymentSuccessful(true);
          setRedirecting(true);
          
          // Get company ID
          const companyId = selectedCompany?.company_id || companyIdRef.current || await getCompanyId();
          
          setTimeout(() => {
            if (companyId) {
              navigate(`/all-filings/${companyId}?payment_success=true`, { replace: true });
            } else {
              navigate('/signin', { replace: true });
            }
          }, 3000);
          return;
        } else if (purchase) {
          // Store company ID from purchase
          if (purchase.org_id) {
            companyIdRef.current = purchase.org_id;
          }
          
          // If purchase status is not paid, redirect to cancel page
          if (purchase.status !== 'paid') {            
            // Redirect immediately - no email
            navigate(`/payment-canceled?purchase_id=${purchaseId}`, { replace: true });
            return;
          }
        }
      } catch (error) {
        console.error('Error verifying purchase:', error);
        setVerifying(false);
        setIsPaymentSuccessful(true);
        setRedirecting(true);
        
        // Get company ID
        const companyId = selectedCompany?.company_id || companyIdRef.current || await getCompanyId();
        
        setTimeout(() => {
          if (companyId) {
            navigate(`/all-filings/${companyId}?payment_success=true`, { replace: true });
          } else {
            navigate('/signin', { replace: true });
          }
        }, 3000);
        return;
      }
    }

    setVerifying(false);
    setIsPaymentSuccessful(true);
    setRedirecting(true);

    // Get company ID before redirecting
    const companyId = selectedCompany?.company_id || companyIdRef.current || await getCompanyId();

    setTimeout(() => {
      if (companyId) {
        navigate(`/all-filings/${companyId}?payment_success=true`, { replace: true });
      } else {
        console.error('No company ID available, redirecting to signin');
        navigate('/signin', { replace: true });
      }
    }, 3000);
  },  [purchaseId, provider, sessionId, billplzPaid, selectedCompany?.company_id, navigate]);

  useEffect(() => {
    verifyAndRedirect();
  }, [verifyAndRedirect]); // Empty dependency array - run only once on mount
  // Show loading/verifying state while checking payment status
  if (verifying) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-bold mb-2 text-gray-900">Verifying Payment...</h2>
          <p className="text-gray-600 text-sm">Please wait while we verify your payment status.</p>
        </div>
      </div>
    );
  }
  
  // Only show success page if payment is actually successful
  if (!isPaymentSuccessful) {
    // This shouldn't happen as we redirect, but just in case
    return null;
  }
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center bg-white p-8 rounded-lg shadow-lg max-w-md">
        <div className="text-green-500 text-6xl mb-4 animate-pulse">✓</div>
        <h2 className="text-2xl font-bold mb-2 text-gray-900">Payment Successful!</h2>
        <p className="text-gray-600 mb-4">Your credits have been added to your wallet.</p>
        {redirecting && (
          <div className="mt-4">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-green-500 mx-auto"></div>
            <p className="text-sm text-gray-500 mt-2">Redirecting...</p>
          </div>
        )}
      </div>
    </div>
  );
}